import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  Skeleton,
  Button,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { API_HOST } from '../../Services/baseUrl';

function DashboardPage() {
  const [dashboard, setDashboard] = useState({ total: 0, companies: [] });
  const [monthlyData, setMonthlyData] = useState([]);
  const [chartToggles, setChartToggles] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const perPage = 5;

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getLast6Months = () => {
    const result = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() + 1 - i, 1);
      result.push(date.toISOString().slice(0, 7)); // "YYYY-MM"
    }
    return result;
  };
  const months = getLast6Months();

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_HOST}third-party-reports/companies-report-counts`);
      setDashboard({
        total: res.data.total || res.data.data.length,
        companies: res.data.data || [],
      });
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyData = async () => {
    try {
      const res = await axios.get(`${API_HOST}third-party-reports/companies-report-monthly`);
      setMonthlyData(res.data.data || []);
    } catch (err) {
      console.error('Failed to load monthly report data', err);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchMonthlyData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/report-count/login');
  };

  const handleToggleChartView = (companyName) => {
    setChartToggles((prev) => ({
      ...prev,
      [companyName]: !prev[companyName],
    }));
  };

  const getInitials = (name = '') =>
    name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const renderMonthlyView = (companyName) => {
    const entry = monthlyData.find((c) => c.name === companyName);
    if (!entry) return null;

    const showChart = chartToggles[companyName];
    const dataPoints = months.map((month) => entry.monthlyCounts[month] || 0);

    return (
      <Box sx={{ mt: 2, pl: 6, pr: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={!!showChart}
                onChange={() => handleToggleChartView(companyName)}
                color="primary"
              />
            }
            label={
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {showChart ? 'Chart View' : 'Grid View'}
              </Typography>
            }
            sx={{ mb: 1 }}
          />
        </Box>

        {showChart ? (
          <Bar
            data={{
              labels: months.map((m) => m.split('-')[1] + '/' + m.split('-')[0].slice(2)),
              datasets: [
                {
                  label: 'Reports',
                  data: dataPoints,
                  backgroundColor: '#1976d2',
                  borderRadius: 6,
                  barThickness: 30,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
              },
              layout: { padding: 10 },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: { stepSize: 1, font: { size: 12 } },
                  grid: { color: '#eaeaea' },
                },
                x: {
                  ticks: { font: { size: 12 } },
                  grid: { color: '#f5f5f5' },
                },
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: `repeat(${months.length}, 1fr)`,
              },
              gap: 1,
            }}
          >
            {months.map((month) => (
              <Box
                key={month}
                sx={{
                  textAlign: 'center',
                  fontSize: 11,
                  color: 'text.secondary',
                  borderRadius: 1,
                  px: 1,
                  py: 0.75,
                  bgcolor: '#f0f4f8',
                  fontWeight: 500,
                }}
              >
                <Typography variant="caption" fontWeight={600} sx={{ color: '#555' }}>
                  {month.split('-')[1]}/{month.split('-')[0].slice(2)}
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} color="primary">
                  {entry.monthlyCounts[month] || 0}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const paginatedCompanies = dashboard.companies.slice(
    (page - 1) * perPage,
    page * perPage
  );

  return (
    <Box sx={{ bgcolor: '#f4f6f8', minHeight: '100vh', pb: 4 }}>
      <AppBar position="static" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Company Dashboard
          </Typography>
          <Button
            onClick={handleLogout}
            color="inherit"
            startIcon={<LogoutIcon />}
            sx={{ textTransform: 'none' }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, px: { xs: 2, sm: 4 } }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
          Total Companies: <span style={{ color: '#1976d2' }}>{dashboard.total}</span>
        </Typography>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box>
            {[...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={60}
                sx={{ mb: 2, borderRadius: 2 }}
              />
            ))}
          </Box>
        ) : paginatedCompanies.length > 0 ? (
          <>
            <Typography variant="h6" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
              Company-wise Report Summary
            </Typography>
            <List>
              {paginatedCompanies.map((c, i) => (
                <Paper
                  key={i}
                  elevation={2}
                  sx={{
                    mb: 2,
                    borderRadius: 3,
                    p: 2,
                    bgcolor: '#fff',
                    boxShadow: '0px 2px 10px rgba(0,0,0,0.04)',
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.01)',
                      boxShadow: '0px 6px 20px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <ListItemButton
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                    onClick={() => navigate(`/company/${encodeURIComponent(c.name)}`)}
                  >
                    <Avatar
                      sx={{
                        bgcolor: '#1976d2',
                        fontSize: 14,
                        fontWeight: 600,
                        width: 42,
                        height: 42,
                      }}
                    >
                      {getInitials(c.companyName || c.name)}
                    </Avatar>

                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {(page - 1) * perPage + i + 1}. {c.companyName || c.name}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="primary">
                        Reports: {c.count}
                      </Typography>
                    </Box>
                  </ListItemButton>

                  {renderMonthlyView(c.companyName || c.name)}
                </Paper>
              ))}
            </List>
          </>
        ) : (
          <Typography variant="body2" sx={{ mt: 4, textAlign: 'center', color: 'gray' }}>
            No companies found.
          </Typography>
        )}

        <Pagination
          count={Math.ceil(dashboard.companies.length / perPage)}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
          sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}
        />
      </Box>
    </Box>
  );
}

export default DashboardPage;
